// A category's controls on the menu page: its ⋮ (Rinomina, Nascondi or Mostra; DD-045, never navigation) and the
// inline rename that replaces its name in the header while it runs.
import { useRef, useState } from "react";
import { DotsVerticalIcon } from "@/components/icons";
import { DropdownMenu } from "@/components/ui/DropdownMenu";
import { cn } from "@/utils/cn";
import type { MenuCategory } from "../types";

export function CategoryMenu({ category, onRename, onToggleHidden }: {
  category: MenuCategory;
  onRename: () => void;
  onToggleHidden: () => void;
}) {
  return (
    <DropdownMenu
      items={[
        { id: "rename", label: "Rinomina", onClick: onRename },
        { id: "hidden", label: category.hidden ? "Mostra" : "Nascondi", onClick: onToggleHidden },
      ]}
      width="w-44"
      align="right"
      // On the page, not in a drawer: a portal keeps the scrolling panel from clipping the menu (RowMenu's rule).
      portal
      menuClassName="rounded-xl"
      trigger={
        <button
          type="button"
          aria-label={`Azioni categoria: ${category.name}`}
          className="grid h-[36px] w-[36px] shrink-0 place-items-center rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-600"
        >
          <DotsVerticalIcon className="h-4 w-4" />
        </button>
      }
    />
  );
}

/**
 * The name as an input: Enter or leaving it saves a changed name, Escape puts the old one back. An empty name saves
 * nothing, since a category always has one.
 */
export function CategoryRename({ name, onCommit, onDone, className }: {
  name: string;
  onCommit: (name: string) => void;
  onDone: () => void;
  className?: string;
}) {
  const [draft, setDraft] = useState(name);
  const settled = useRef(false);
  const settle = (save: boolean) => {
    if (settled.current) return;
    settled.current = true;
    const next = draft.trim();
    if (save && next !== "" && next !== name) onCommit(next);
    onDone();
  };
  return (
    <input
      type="text"
      value={draft}
      autoFocus
      enterKeyHint="done"
      aria-label="Nome della categoria"
      onChange={(e) => setDraft(e.target.value)}
      onBlur={() => settle(true)}
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          e.preventDefault();
          settle(true);
        } else if (e.key === "Escape") {
          e.preventDefault();
          settle(false);
        }
      }}
      className={cn(
        "h-[36px] min-w-0 flex-1 rounded-lg bg-white px-2 text-[16px] font-semibold text-gray-900 outline-none ring-2 ring-indigo-300",
        className,
      )}
    />
  );
}
